import {
  ConciergeBillExpenses,
  ConciergeTicket,
  ConciergeTicketDocument,
  ConciergeTicketItem,
  ConciergeTicketMyStats,
  ConciergeTicketStats,
  ExpenseApproverOption,
  ExpenseApproverOptions,
  FinancePocOption,
  OpsAssignee,
  OpsFilterOptions,
  OpsTicketClassification,
  StatusHistory,
  TicketFinancePayment,
  TicketHistoryItem,
} from "../types/concierge";
import { Pagination } from "../types/pagination";
import { IConciergeService } from "../types/services/concierge-service";
import { DELETE, FetchResult, GET, PATCH, POST } from "../utils/fetch";

type TicketListingResponse = {
  tickets: any[];
  paginate: Pagination & { total_count?: number };
};

type TicketStatsResponse = {
  stats: ConciergeTicketStats;
};

type TicketResponse = {
  ticket: any;
};

type TicketDocumentsResponse = {
  documents: any[];
};

type TicketExpenseApprovalsResponse = {
  expense_approvals: any[];
  meta: {
    error: boolean;
  };
};

type StatusHistoriesResponse = {
  status_histories: any[];
};

// options and default_approver are flat at the top level, not nested under a key
type ExpenseApproverOptionsResponse = {
  options: ExpenseApproverOption[];
  default_approver: ExpenseApproverOption | null;
  meta: {
    error: boolean;
  };
};

type TicketFinancePaymentsResponse = {
  finance_payments: any[];
};

type TicketFinancePaymentResponse = {
  finance_payment: any;
};

type FinancePocOptionsResponse = {
  options: FinancePocOption[];
};

class ConciergeService implements IConciergeService {
  async getTickets(
    assignee_id?: string[],
    properties?: string[],
    department?: string[],
    ticket_type?: string[],
    status?: string[],
    search?: string,
    page?: string,
    page_size?: string,
    sort?: string,
    cities?: string[],
    start_date?: string,
    end_date?: string,
    priority?: string[],
    budget_expense?: string[],
    reported_by?: string[],
    cluster?: string[],
    finance_pending?: boolean,
  ): Promise<FetchResult<ConciergeTicketItem[]>> {
    const qp = new URLSearchParams();

    if (assignee_id?.length) {
      assignee_id.forEach((id) => qp.append("assignee_id[]", id));
    }

    if (properties?.length) {
      properties.forEach((each) => qp.append("property_id[]", each));
    }

    if (department?.length) {
      department.forEach((id) => qp.append("ops_department_id[]", id));
    }

    if (ticket_type?.length) {
      ticket_type.forEach((t) => qp.append("kind[]", t));
    }

    // Finance Pending covers every status, so it sends none
    if (status?.length && !finance_pending) {
      status.forEach((each) => qp.append("status[]", each));
    }

    if (finance_pending) {
      qp.set("finance_pending", "true");
    }

    if (page) {
      qp.set("page", page);
    }

    if (page_size) {
      qp.set("per_page", page_size);
    }

    if (search) {
      qp.set("query", search);
    }

    if (sort) {
      const SORT_MAP: Record<string, { column: string; order: string }> = {
        created_at_asc: { column: "created_at", order: "asc" },
        created_at_desc: { column: "created_at", order: "desc" },
        due_at_future: { column: "due_at", order: "asc" },
        due_at_past: { column: "due_at", order: "desc" },
      };
      const mapped = SORT_MAP[sort];
      if (mapped) {
        qp.set("sort_column", mapped.column);
        qp.set("sort_order", mapped.order);
      }
    }

    if (cities?.length) {
      cities.forEach((each) => qp.append("location_id[]", each));
    }

    if (start_date) {
      qp.set("date_start", start_date);
    }

    if (end_date) {
      qp.set("date_end", end_date);
    }

    if (priority?.length) {
      priority.forEach((p) => qp.append("priority[]", p));
    }

    if (budget_expense?.length) {
      budget_expense.forEach((b) => qp.append("budget_expense[]", b));
    }

    if (reported_by?.length) {
      reported_by.forEach((id) => qp.append("reported_by_id[]", id));
    }

    if (cluster?.length) {
      cluster.forEach((id) => qp.append("ops_cluster_id[]", id));
    }

    const marshalled = qp.toString();

    let url = "/api/v2/ops/tickets";

    if (marshalled) {
      url = `${url}?${marshalled}`;
    }

    const { error, response } = await GET<TicketListingResponse>(url);

    if (error) {
      return {
        error,
        paginate: undefined,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };
    }

    const paginate = response.paginate,
      formatted = response.tickets.map((each) => {
        const {
            slug,
            title,
            due_at,
            assignee,
            property,
            priority,
            status,
            created_at,
            ticket_number,
            assigned_at,
            kind,
            ticket_type,
            cluster,
            cluster_name,
            issue_type,
            source,
            department,
            category_id,
            sub_category_id,
            allowed_actions,
          } = each,
          formatted: ConciergeTicketItem = {
            id: ticket_number || 0,
            slug: slug || "",
            status: status === "reopened" ? "open" : status || "",
            priority: priority?.toLowerCase() || "p3",
            title: title || "",
            assignee: assignee?.value || "",
            due_date: due_at || "",
            reported_date: created_at || "",
            property_name: property?.value || property?.name || "",
            assigned_date: assigned_at || "",
            ticket_number: String(ticket_number || ""),
            ticket_type: kind || "",
            cluster_name: cluster?.value || cluster?.name || cluster_name || "",
            issue_type: issue_type || "",
            source: ticket_type || source || "",
            department:
              department?.name ||
              (typeof department === "string" ? department : "") ||
              "",
            category: category_id ? String(category_id) : "",
            subcategory: sub_category_id ? String(sub_category_id) : "",
            property_id: property?.id ? String(property.id) : "",
            allowed_actions: allowed_actions || [],
          };

        return formatted;
      });

    return {
      error: null,
      paginate: {
        ...paginate,
        total_records: paginate.total_records || paginate.total_count || 0,
      },
      response: formatted,
    };
  }

  async getMyTickets(
    tab: string,
    page?: string,
    due_start?: string,
    search?: string,
    department?: string,
    assignee_id?: string,
  ): Promise<FetchResult<ConciergeTicketItem[]>> {
    const qp = new URLSearchParams();

    qp.set("tab", tab);

    const SORT: Record<string, { column: string; order: string }> = {
      my_actions: { column: "due_at", order: "asc" },
      upcoming: { column: "due_at", order: "asc" },
      approvals: { column: "created_at", order: "desc" },
      closed: { column: "created_at", order: "desc" },
    };
    const sort = SORT[tab] || SORT.my_actions;
    qp.set("sort_column", sort.column);
    qp.set("sort_order", sort.order);

    if (page) qp.set("page", page);
    qp.set("per_page", "10");
    if (due_start) qp.set("due_start", due_start);
    if (search) qp.set("query", search);
    if (department) qp.set("department", department);
    if (assignee_id) qp.set("assignee_id", assignee_id);

    const { error, response } = await GET<TicketListingResponse>(
      `/api/v2/hospitality_ops/tickets?${qp.toString()}`,
    );

    if (error) return { error, paginate: undefined, response: null };
    if (!response)
      return {
        error: new Error("Unknown error"),
        paginate: undefined,
        response: null,
      };

    const paginate = response.paginate,
      formatted = response.tickets.map((each) => {
        const {
            slug,
            title,
            due_at,
            assignee,
            property,
            priority,
            status,
            created_at,
            ticket_number,
            assigned_at,
            kind,
            ticket_type,
            cluster,
            cluster_name,
            issue_type,
            source,
            department,
            category_id,
            sub_category_id,
            allowed_actions,
          } = each,
          item: ConciergeTicketItem = {
            id: ticket_number || 0,
            slug: slug || "",
            status: status === "reopened" ? "open" : status || "",
            priority: priority?.toLowerCase() || "p3",
            title: title || "",
            assignee: assignee?.value || "",
            due_date: due_at || "",
            reported_date: created_at || "",
            property_name: property?.value || property?.name || "",
            assigned_date: assigned_at || "",
            ticket_number: String(ticket_number || ""),
            ticket_type: kind || "",
            cluster_name: cluster?.value || cluster?.name || cluster_name || "",
            issue_type: issue_type || "",
            source: ticket_type || source || "",
            department:
              department?.name ||
              (typeof department === "string" ? department : "") ||
              "",
            category: category_id ? String(category_id) : "",
            subcategory: sub_category_id ? String(sub_category_id) : "",
            property_id: property?.id ? String(property.id) : "",
            allowed_actions: allowed_actions || [],
          };
        return item;
      });

    return {
      error: null,
      paginate: {
        ...paginate,
        total_records: paginate.total_records || paginate.total_count || 0,
      },
      response: formatted,
    };
  }

  async getTicketStats(
    assignee_id?: string[],
    properties?: string[],
    department?: string[],
    ticket_type?: string[],
    search?: string,
    priority?: string[],
    budget_expense?: string[],
    reported_by?: string[],
    cluster?: string[],
    cities?: string[],
    start_date?: string,
    end_date?: string,
  ): Promise<FetchResult<ConciergeTicketStats>> {
    const qp = new URLSearchParams();

    if (assignee_id?.length) {
      assignee_id.forEach((id) => qp.append("assignee_id[]", id));
    }
    if (properties?.length) {
      properties.forEach((p) => qp.append("property_id[]", p));
    }
    if (department?.length) {
      department.forEach((id) => qp.append("ops_department_id[]", id));
    }
    if (ticket_type?.length) {
      ticket_type.forEach((t) => qp.append("kind[]", t));
    }
    if (search) {
      qp.set("query", search);
    }
    if (priority?.length) {
      priority.forEach((p) => qp.append("priority[]", p));
    }
    if (budget_expense?.length) {
      budget_expense.forEach((b) => qp.append("budget_expense[]", b));
    }
    if (reported_by?.length) {
      reported_by.forEach((id) => qp.append("reported_by_id[]", id));
    }
    if (cluster?.length) {
      cluster.forEach((id) => qp.append("ops_cluster_id[]", id));
    }
    if (cities?.length) {
      cities.forEach((c) => qp.append("location_id[]", c));
    }
    if (start_date) {
      qp.set("date_start", start_date);
    }
    if (end_date) {
      qp.set("date_end", end_date);
    }

    const marshalled = qp.toString(),
      url = marshalled
        ? `/api/v2/ops/tickets/stats?${marshalled}`
        : "/api/v2/ops/tickets/stats",
      { error, response } = await GET<TicketStatsResponse>(url);

    if (error) {
      return { error, response: null };
    }
    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response: response.stats };
  }

  async getMyTicketStats(
    department?: string,
    assignee_id?: string,
  ): Promise<FetchResult<ConciergeTicketMyStats>> {
    const qp = new URLSearchParams();

    if (department) qp.set("department", department);
    if (assignee_id) qp.set("assignee_id", assignee_id);

    const { error, response } = await GET<{ stats: ConciergeTicketMyStats }>(
      `/api/v2/hospitality_ops/tickets/stats?${qp}`,
    );

    if (error) return { error, response: null };
    if (!response) return { error: new Error("Unknown error"), response: null };

    return { error: null, response: response.stats };
  }

  async getTicket(
    opportunitySlug: string,
  ): Promise<FetchResult<ConciergeTicket>> {
    const url = `/api/v2/hospitality_ops/tickets/${opportunitySlug}`,
      { error, response } = await GET<TicketResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const {
        id,
        ticket_number,
        title,
        description,
        slug,
        ticket_type,
        visible_to_ho,
        due_at,
        priority,
        status,
        service_type,
        reported_by_id,
        category_id,
        sub_category_id,
        created_at,
        issue_type,
        verification_required,
        ops_cluster_id,
        kind,
        allowed_actions,
        allowed_statuses,
        resolve_reasons,
        // object fields from new endpoint
        assignee,
        property,
        department,
        reported_by,
        author,
        home_owner_manager,
        expense_approvals,
        cluster,
      } = response.ticket,
      formatted: ConciergeTicket = {
        id: id || 0,
        slug: slug || "",
        ticket_number: ticket_number || "",
        title: title || "",
        description: description || "",
        status: status || "",
        priority: priority?.toLowerCase() || "p3",
        department: department?.id ? String(department.id) : "",
        service_type: service_type || "non_chargeable",
        property_id: property?.id || 0,
        ticket_type: ticket_type || "",
        visible_to_ho: visible_to_ho || false,
        property_name: property?.value || "",
        due_date: due_at || "",
        assignee_id: assignee?.id,
        assignee: assignee?.value || "",
        reported_by: reported_by?.value || "",
        reported_by_id: reported_by_id || 0,
        author: author?.value || "",
        author_id: author?.id || 0,
        category: category_id ? String(category_id) : "",
        subcategory: sub_category_id ? String(sub_category_id) : "",
        created_at: created_at || "",
        expense_id: expense_approvals?.[0]?.id,
        budgeted_cost: expense_approvals?.[0]?.budgeted_cost || 0,
        approved_cost: expense_approvals?.[0]?.approved_cost || 0,
        property_type: "",
        hom_manager: home_owner_manager?.value || "",
        hom_manager_id: home_owner_manager?.id,
        issue_type: issue_type || "",
        verification_required: verification_required || false,
        kind: kind || "",
        allowed_actions: allowed_actions || [],
        allowed_statuses: allowed_statuses || [],
        ops_department_id: department?.id || undefined,
        ops_cluster_id: ops_cluster_id || cluster?.id || undefined,
        resolve_reasons: resolve_reasons || [],
      };

    return {
      error: null,
      response: formatted,
    };
  }


  async updateTicket(
    slug: string,
    priority?: string,
    property_id?: number,
    ticket_type?: string,
    visible_to_ho?: boolean,
    status?: string,
    department?: string,
    service_type?: string,
    title?: string,
    description?: string,
    due_date?: string,
    reported_by_id?: string,
    hold_reason?: string,
    hold_reopen_date?: string,
    reopen_due_date?: string,
    category?: string,
    subcategory?: string,
    expense_id?: number,
    budgeted_cost?: number,
    approved_cost?: number,
    send_email?: boolean,
    property_type?: string,
    issue_type?: string,
    verification_required?: boolean,
  ): Promise<FetchResult<void>> {
    const template = [
        {
          key: "title",
          value: title,
        },
        {
          key: "description",
          value: description,
        },
        {
          key: "ops_department_id",
          value: department ? Number(department) : undefined,
        },
        {
          key: "service_type",
          value: service_type,
        },
        {
          key: "due_at",
          value: reopen_due_date || due_date,
        },
        {
          key: "status",
          value: status,
        },
        {
          key: "property_id",
          value: property_id,
        },
        {
          key: "ticket_type",
          value: ticket_type,
        },
        {
          key: "visible_to_ho",
          value: visible_to_ho,
        },
        {
          key: "priority",
          value: priority,
        },
        {
          key: "reported_by_id",
          value: reported_by_id,
        },
        {
          key: "hold_reason",
          value: hold_reason,
        },
        {
          key: "hold_expiry",
          value: hold_reopen_date,
        },
        {
          key: "category_id",
          value: category,
        },
        {
          key: "sub_category_id",
          value: subcategory,
        },
        {
          key: "expense_approval_attributes",
          value: {
            id: Number(expense_id) || null,
            budgeted_cost:
              Number(budgeted_cost) > 0 ? Number(budgeted_cost) : null,
            approved_cost:
              Number(approved_cost) > 0 ? Number(approved_cost) : null,
          },
        },
        {
          key: "send_email",
          value: send_email,
        },
        {
          key: "property_type",
          value: property_type,
        },
        {
          key: "issue_type",
          value: issue_type,
        },
        {
          key: "verification_required",
          value: verification_required,
        },
      ],
      payload = template.reduce((compiled, each) => {
        const { key, value } = each;

        if (value !== undefined) {
          compiled[key] = value;
        }

        return compiled;
      }, {} as { [k: string]: string | number | string[] | {} }),
      url = `/api/v2/concierge/${slug}`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async editTicket(
    slug: string,
    title?: string,
    description?: string,
    department?: string,
    subcategory?: string,
    priority?: string,
    due_date?: string,
    ticket_type?: string,
    visible_to_ho?: boolean,
    reported_by_id?: string,
    service_type?: string,
    issue_type?: string,
    verification_required?: boolean,
    property_id?: number,
    confirm?: boolean,
  ): Promise<FetchResult<void>> {
    const template = [
        { key: "title", value: title },
        { key: "description", value: description },
        {
          key: "ops_department_id",
          value: department ? Number(department) : undefined,
        },
        { key: "sub_category_id", value: subcategory },
        { key: "priority", value: priority },
        { key: "due_at", value: due_date },
        { key: "ticket_type", value: ticket_type },
        { key: "visible_to_ho", value: visible_to_ho },
        { key: "reported_by_id", value: reported_by_id },
        { key: "service_type", value: service_type },
        { key: "issue_type", value: issue_type },
        { key: "verification_required", value: verification_required },
        { key: "property_id", value: property_id },
        { key: "confirm", value: confirm },
      ],
      payload = template.reduce((compiled, each) => {
        const { key, value } = each;

        if (value !== undefined) {
          compiled[key] = value;
        }

        return compiled;
      }, {} as { [k: string]: any }),
      url = `/api/v2/hospitality_ops/tickets/${slug}/edit`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return { error, response: null };
    }

    return { error: null, response: null };
  }

  async closeTicket(
    slug: string,
    closure_reason_id?: string,
    closure_comment?: string,
    file?: File | null,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/hospitality_ops/tickets/${slug}/close`,
      fd = new FormData();

    if (closure_reason_id) fd.append("closure_reason_id", closure_reason_id);
    if (closure_comment) fd.append("closure_comment", closure_comment);
    if (file) fd.append("documents_attributes[0][file]", file);

    const { error } = await PATCH<void>(url, fd);

    if (error) {
      return { error, response: null };
    }

    return { error: null, response: null };
  }

  async resolveTicket(
    slug: string,
    closure_reason_id: string,
    closure_comment?: string,
    file?: File | null,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/hospitality_ops/tickets/${slug}/resolve`,
      fd = new FormData();

    fd.append("closure_reason_id", String(Number(closure_reason_id)));
    if (closure_comment) fd.append("closure_comment", closure_comment);
    if (file) fd.append("documents_attributes[0][file]", file);

    const { error } = await PATCH<void>(url, fd);

    if (error) {
      return { error, response: null };
    }

    return { error: null, response: null };
  }

  async dismissTicket(
    slug: string,
    closure_reason_id: string,
    closure_comment?: string,
    file?: File | null,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/hospitality_ops/tickets/${slug}/dismiss`,
      fd = new FormData();

    fd.append("closure_reason_id", closure_reason_id);
    if (closure_comment) fd.append("closure_comment", closure_comment);
    if (file) fd.append("documents_attributes[0][file]", file);

    const { error } = await PATCH<void>(url, fd);

    if (error) {
      return { error, response: null };
    }

    return { error: null, response: null };
  }

  async reopenTicket(
    slug: string,
    closure_reason_id: string,
    priority: string,
    due_at: string,
    assignee_id?: string,
  ): Promise<FetchResult<void>> {
    const payload: Record<string, any> = {
      closure_reason_id: Number(closure_reason_id),
      priority,
      due_at,
    };
    if (assignee_id) payload.assignee_id = Number(assignee_id);

    const url = `/api/v2/hospitality_ops/tickets/${slug}/reopen_closed`,
      { error } = await PATCH<void>(url, payload);

    if (error) return { error, response: null };
    return { error: null, response: null };
  }

  async reopenResolvedTicket(
    slug: string,
    closure_reason_id: string,
    priority: string,
    due_at: string,
    assignee_id?: string,
  ): Promise<FetchResult<void>> {
    const payload: Record<string, any> = {
      closure_reason_id: Number(closure_reason_id),
      priority,
      due_at,
    };
    if (assignee_id) payload.assignee_id = Number(assignee_id);

    const url = `/api/v2/hospitality_ops/tickets/${slug}/reopen_resolved`,
      { error } = await PATCH<void>(url, payload);

    if (error) return { error, response: null };
    return { error: null, response: null };
  }

  async convertTicket(
    slug: string,
    sub_category_id: string,
    department_id: string,
    assignee_id: string,
    priority: string,
    due_at: string,
    visible_to_ho: boolean,
    verification_required?: boolean,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/hospitality_ops/tickets/${slug}/convert`,
      payload: Record<string, string | boolean> = { assignee_id };

    if (sub_category_id) payload.sub_category_id = sub_category_id;
    if (department_id) payload.department_id = department_id;
    if (priority) payload.priority = priority;
    if (due_at) payload.due_at = due_at;
    payload.visible_to_ho = visible_to_ho;
    if (verification_required !== undefined) payload.verification_required = verification_required;

    const { error } = await PATCH<void>(url, payload);

    if (error) {
      return { error, response: null };
    }

    return { error: null, response: null };
  }

  async getOpsAssignees(
    department_id?: string,
    cluster_ids?: string[],
  ): Promise<FetchResult<OpsAssignee[]>> {
    const qp = new URLSearchParams();
    if (department_id) qp.append("department_id", department_id);
    cluster_ids?.forEach((id) => qp.append("cluster_ids[]", id));

    const url = `/api/v2/hospitality_ops/tickets/assignees?${qp}`,
      { error, response } = await GET<{ assignees: OpsAssignee[] }>(url);

    if (error) return { error, response: null };
    if (!response) return { error: new Error("Unknown error"), response: null };

    return { error: null, response: response.assignees };
  }

  async suggestClassification(
    title: string,
    description: string,
  ): Promise<FetchResult<OpsTicketClassification>> {
    const url = `/api/v2/hospitality_ops/tickets/suggest_classification`,
      { error, response } = await POST<{ suggestion: OpsTicketClassification }>(
        url,
        { title, description },
      );

    if (error) return { error, response: null };
    if (!response) return { error: new Error("Unknown error"), response: null };

    return { error: null, response: response.suggestion };
  }

  async getOpsFilterOptions(): Promise<FetchResult<OpsFilterOptions>> {
    const url = `/api/v2/hospitality_ops/tickets/filter_options`,
      { error, response } = await GET<OpsFilterOptions & { meta: any }>(url);

    if (error) return { error, response: null };
    if (!response) return { error: new Error("Unknown error"), response: null };

    return {
      error: null,
      response: {
        clusters: response.clusters || [],
        properties: response.properties || [],
      },
    };
  }

  async createOpsTicket(
    kind: string,
    property_id: string,
    issue_type: string,
    ticket_type: string,
    title: string,
    description: string,
    department_id?: string,
    sub_category_id?: string,
    visible_to_ho?: boolean,
    assignee_id?: string,
    priority?: string,
    due_at?: string,
    file?: File,
    reported_by_id?: number,
    verification_required?: boolean,
  ): Promise<FetchResult<string>> {
    const url = `/api/v2/hospitality_ops/tickets`,
      fd = new FormData();

    fd.append("kind", kind);
    fd.append("property_id", property_id);
    fd.append("issue_type", issue_type);
    fd.append("ticket_type", ticket_type);
    fd.append("title", title);
    fd.append("description", description);
    if (department_id) fd.append("department_id", department_id);
    if (sub_category_id) fd.append("sub_category_id", sub_category_id);
    if (visible_to_ho !== undefined)
      fd.append("visible_to_ho", String(visible_to_ho));
    if (assignee_id) fd.append("assignee_id", assignee_id);
    if (priority) fd.append("priority", priority);
    if (due_at) fd.append("due_at", due_at);
    if (file) fd.append("documents_attributes[0][file]", file);
    if (reported_by_id) fd.append("reported_by_id", String(reported_by_id));
    if (verification_required !== undefined)
      fd.append("verification_required", String(verification_required));

    const { error, response } = await POST<{ ticket: { slug: string } }>(
      url,
      fd,
    );

    if (error) return { error, response: null };
    if (!response) return { error: new Error("Unknown error"), response: null };

    return { error: null, response: response.ticket.slug };
  }

  async updatePOC(
    opportunity_slug: string,
    assignee_id?: number,
  ): Promise<FetchResult<void>> {
    const payload = { assignee_id: assignee_id },
      url = `/api/v2/hospitality_ops/tickets/${opportunity_slug}/update_poc`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async getTicketDocuments(
    opportunity_slug: string,
  ): Promise<FetchResult<ConciergeTicketDocument[]>> {
    const url = `/api/v2/concierge/${opportunity_slug}/documents`;

    const { error, response } = await GET<TicketDocumentsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const formatted = response.documents.map((each) => {
      const { id, link, created_at, title, content_type } = each,
        formatted: ConciergeTicketDocument = {
          id: id,
          file: link || "",
          created_at: created_at || "",
          title: title || "",
          content_type: content_type || "",
        };

      return formatted;
    });

    return {
      error: null,
      response: formatted,
    };
  }

  async addTicketDocument(
    opportunity_slug: string,
    file: File,
  ): Promise<FetchResult<void>> {
    const formData = new FormData();

    formData.set("file", file);

    const url = `/api/v2/concierge/${opportunity_slug}/documents`,
      { error } = await POST<void>(url, formData);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async deleteTicketDocument(
    opportunity_slug: string,
    document_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/concierge/${opportunity_slug}/documents/${document_id}`,
      { error } = await DELETE<void>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  downloadAllTickets(opp_slug: string): string {
    const url = `/api/v2/concierge/${opp_slug}/download_all.zip`;

    return url;
  }

  async getBillingExpenses(
    opportunitySlug: string,
  ): Promise<FetchResult<ConciergeBillExpenses[]>> {
    const url = `/api/v2/concierge/${opportunitySlug}/ticket_expense_approvals`,
      { error, response } = await GET<TicketExpenseApprovalsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const formatted = response.expense_approvals.map((each) => {
      const {
          id,
          requested_budget,
          approved_budget,
          description,
          status,
          created_at,
          created_by_name,
          quotation,
          comments,
          can_update,
          can_approve,
          can_object,
          can_delete,
          expense_department,
          assigned_approver_id,
          assigned_approver_name,
        } = each,
        formattedComments = (comments || []).map((comment: any) => ({
          id: comment.id || 0,
          content: comment.content || "",
          author: comment.author || "",
          created_at: comment.created_at || "",
        })),
        // Map all quotations to attachments
        formattedAttachments = (quotation || []).map((quote: any) => ({
          id: quote.id,
          file: quote.link,
          link: quote.link,
          document_name: quote.document_name,
          document_identifier: quote.document_identifier,
          name: quote.name,
        })),
        // Use first quotation as attachment for backward compatibility
        attachment =
          formattedAttachments.length > 0 ? formattedAttachments[0] : undefined,
        formatted: ConciergeBillExpenses = {
          id: id,
          amount: approved_budget || requested_budget || 0,
          attachment: attachment,
          attachments:
            formattedAttachments.length > 0 ? formattedAttachments : undefined,
          base_amount: String(requested_budget || ""),
          bill_date: created_at || "",
          bill_number: "",
          gst_percentage: "",
          name: description || "",
          status: status || "",
          description: description || "",
          expense_type_id: 0,
          created_by: created_by_name || "",
          created_at: created_at || "",
          comments:
            formattedComments.length > 0 ? formattedComments : undefined,
          can_update: can_update || false,
          can_approve: can_approve || false,
          can_object: can_object || false,
          can_delete: can_delete || false,
          expense_department: expense_department || null,
          assigned_approver_id: assigned_approver_id || null,
          assigned_approver_name: assigned_approver_name || null,
        };

      return formatted;
    });

    return {
      error: null,
      response: formatted,
    };
  }

  async addBillingExpenses(
    ticket_slug: string,
    bill_number: number,
    nature_of_expense: string,
    base_amount?: string,
    status?: string,
    description?: string,
    billGeneratedDate?: string,
    gst_percentage?: number,
    attachment_file?: File | string,
  ): Promise<FetchResult<void>> {
    const payload = new FormData();

    if (base_amount) {
      payload.set("base_amount", base_amount);
    }
    if (bill_number) {
      payload.set("bill_number", bill_number.toString());
    }
    if (status) {
      payload.set("status", status);
    }
    if (description) {
      payload.set("description", description);
    }
    if (gst_percentage) {
      payload.set("gst_percentage", gst_percentage.toString());
    }
    if (billGeneratedDate) {
      payload.set("bill_date", billGeneratedDate);
    }
    if (nature_of_expense) {
      payload.set("expense_type_id", nature_of_expense);
    }
    if (attachment_file instanceof File) {
      payload.set("attachment_attributes[file]", attachment_file);
    }

    const url = `/api/v2/concierge/${ticket_slug}/expenses`,
      { error } = await POST<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async updateBillingExpense(
    ticket_slug: string,
    id: number,
    bill_number: number,
    nature_of_expense: string,
    base_amount?: string,
    billGeneratedDate?: string,
    gst_percentage?: number,
    status?: string,
    description?: string,
    attachment_file?: File | string,
    attachmentId?: number,
  ): Promise<FetchResult<void>> {
    const payload = new FormData();

    if (base_amount) {
      payload.set("base_amount", base_amount);
    }
    if (bill_number) {
      payload.set("bill_number", bill_number.toString());
    }
    if (status) {
      payload.set("status", status);
    }
    if (description) {
      payload.set("description", description);
    }
    if (gst_percentage) {
      payload.set("gst_percentage", gst_percentage.toString());
    }
    if (billGeneratedDate) {
      payload.set("bill_date", billGeneratedDate);
    }
    if (nature_of_expense) {
      payload.set("expense_type_id", nature_of_expense);
    }
    if (attachmentId) {
      payload.set("attachment_attributes[id]", attachmentId.toString());
    }
    if (attachment_file instanceof File) {
      payload.set("attachment_attributes[file]", attachment_file);
    }

    if (attachmentId !== undefined && attachment_file === null) {
      payload.set("attachment_attributes[_destroy]", "true");
    }

    const url = `/api/v2/concierge/${ticket_slug}/expenses/${id}`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async getStatusHistories(
    ticket_slug: string,
  ): Promise<FetchResult<StatusHistory[]>> {
    const url = `/api/v2/concierge/${ticket_slug}/ticket_expense_approvals/status_histories`,
      { error, response } = await GET<StatusHistoriesResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const formatted: StatusHistory[] = (response.status_histories || []).map(
      (each) => ({
        id: each.id || 0,
        old_status: each.old_status || "",
        new_status: each.new_status || "",
        created_at: each.created_at || "",
        resource_type: each.resource_type || "",
        resource_id: each.resource_id || 0,
        actor_name: each.actor_name || "",
        actor_type: each.actor_type || "",
        ticket_title: each.ticket_title || null,
        ticket_slug: each.ticket_slug || null,
        expense_approval_description: each.expense_approval_description || null,
        event: each.event || null,
        change_set: each.change_set || {},
        approver_names: each.approver_names || null,
      }),
    );

    return {
      error: null,
      response: formatted,
    };
  }

  async getTicketHistory(
    slug: string,
  ): Promise<FetchResult<TicketHistoryItem[]>> {
    const url = `/api/v2/hospitality_ops/tickets/${slug}/history`,
      { error, response } = await GET<{ history: any[] }>(url);

    if (error) return { error, response: null };
    if (!response) return { error: new Error("Unknown error"), response: null };

    const mapped: TicketHistoryItem[] = (response.history || []).map(
      (h: any) => ({
        type: h.type || "",
        at: h.at || "",
        actor: {
          type: h.actor?.type || "",
          id: h.actor?.id || 0,
          name: h.actor?.name || "",
        },
        detail: h.detail
          ? {
              from: h.detail.from || undefined,
              to: h.detail.to || undefined,
              finance_poc: h.detail.finance_poc || undefined,
              release_note: h.detail.release_note || undefined,
            }
          : null,
      }),
    );

    return { error: null, response: mapped };
  }

  async addExpenseComment(
    ticket_slug: string,
    expense_approval_id: number,
    content: string,
  ): Promise<FetchResult<void>> {
    const payload = new FormData();
    payload.set("content", content);

    const url = `/api/v2/concierge/${ticket_slug}/ticket_expense_approvals/${expense_approval_id}/add_comment`,
      { error } = await POST<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async updateExpenseApprovalStatus(
    ticket_slug: string,
    expense_approval_id: number,
    status: "approved" | "rejected",
    comment: string,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/concierge/${ticket_slug}/ticket_expense_approvals/${expense_approval_id}/update_status`,
      { error } = await PATCH<void>(url, {
        status: status,
        comment: comment || "",
      });

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async createExpenseApproval(
    ticket_slug: string,
    budgeted_cost: string,
    description: string,
    attachments?: File[],
    expense_department?: string,
    approver_id?: number,
  ): Promise<FetchResult<void>> {
    const payload = new FormData();

    payload.set("budgeted_cost", budgeted_cost);
    payload.set("description", description);

    if (expense_department) {
      payload.set("expense_department", expense_department);
    }
    if (approver_id) {
      payload.set("approver_id", approver_id.toString());
    }

    if (attachments && attachments.length > 0) {
      attachments.forEach((file, index) => {
        payload.set(`attachments_attributes[${index}][file]`, file);
      });
    }

    const url = `/api/v2/concierge/${ticket_slug}/ticket_expense_approvals`,
      { error } = await POST<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async updateExpenseApproval(
    ticket_slug: string,
    expense_approval_id: number,
    budgeted_cost?: string,
    description?: string,
    attachments?: Array<{ id?: number; file?: File; _destroy?: boolean }>,
    expense_department?: string,
    approver_id?: number,
  ): Promise<FetchResult<void>> {
    const payload = new FormData();

    if (budgeted_cost !== undefined) {
      payload.set("budgeted_cost", budgeted_cost);
    }
    if (description !== undefined) {
      payload.set("description", description);
    }
    if (expense_department !== undefined) {
      payload.set("expense_department", expense_department);
    }
    if (approver_id !== undefined) {
      payload.set("approver_id", approver_id.toString());
    }

    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment, index) => {
        if (attachment._destroy && attachment.id) {
          // Delete existing attachment
          payload.set(
            `attachments_attributes[${index}][id]`,
            attachment.id.toString(),
          );
          payload.set(`attachments_attributes[${index}][_destroy]`, "1");
        } else if (attachment.id && attachment.file) {
          // Update existing attachment with new file
          payload.set(
            `attachments_attributes[${index}][id]`,
            attachment.id.toString(),
          );
          payload.set(
            `attachments_attributes[${index}][file]`,
            attachment.file,
          );
        } else if (attachment.id && !attachment.file) {
          // Keep existing attachment as is
          payload.set(
            `attachments_attributes[${index}][id]`,
            attachment.id.toString(),
          );
        } else if (attachment.file && !attachment.id) {
          // Add new attachment
          payload.set(
            `attachments_attributes[${index}][file]`,
            attachment.file,
          );
        }
      });
    }

    const url = `/api/v2/concierge/${ticket_slug}/ticket_expense_approvals/${expense_approval_id}`,
      { error } = await PATCH<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async deleteExpenseApproval(
    ticket_slug: string,
    expense_approval_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/concierge/${ticket_slug}/ticket_expense_approvals/${expense_approval_id}`,
      { error } = await DELETE<void>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async getExpenseApproverOptions(
    ticket_slug: string,
    department: string,
  ): Promise<FetchResult<ExpenseApproverOptions>> {
    const qp = new URLSearchParams();
    qp.set("department", department);

    const url = `/api/v2/concierge/${ticket_slug}/ticket_expense_approvals/approver_options?${qp.toString()}`,
      { error, response } = await GET<ExpenseApproverOptionsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: {
        options: response.options || [],
        default_approver: response.default_approver || null,
      },
    };
  }

  async getFinancePayments(
    ticket_slug: string,
  ): Promise<FetchResult<TicketFinancePayment[]>> {
    const url = `/api/v2/concierge/${ticket_slug}/ticket_finance_payments`,
      { error, response } = await GET<TicketFinancePaymentsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: (response.finance_payments || []).map(toFinancePayment),
    };
  }

  async createFinancePayment(
    ticket_slug: string,
    payload: { note: string; finance_poc_id: number; attachments?: File[] },
  ): Promise<FetchResult<TicketFinancePayment>> {
    const formData = new FormData();

    formData.set("note", payload.note);
    formData.set("finance_poc_id", payload.finance_poc_id.toString());

    payload.attachments?.forEach((file, index) => {
      formData.set(`attachments_attributes[${index}][file]`, file);
    });

    const url = `/api/v2/concierge/${ticket_slug}/ticket_finance_payments`,
      { error, response } = await POST<TicketFinancePaymentResponse>(
        url,
        formData,
      );

    return toFinancePaymentResult(error, response);
  }

  async updateFinancePayment(
    ticket_slug: string,
    finance_payment_id: number,
    payload: {
      note?: string;
      finance_poc_id?: number;
      attachments?: Array<{ id?: number; file?: File; _destroy?: boolean }>;
    },
  ): Promise<FetchResult<TicketFinancePayment>> {
    const formData = new FormData();

    if (payload.note !== undefined) {
      formData.set("note", payload.note);
    }
    if (payload.finance_poc_id !== undefined) {
      formData.set("finance_poc_id", payload.finance_poc_id.toString());
    }

    // a removed document sends its id with _destroy, a new one sends its file
    payload.attachments?.forEach((attachment, index) => {
      if (attachment._destroy && attachment.id) {
        formData.set(
          `attachments_attributes[${index}][id]`,
          attachment.id.toString(),
        );
        formData.set(`attachments_attributes[${index}][_destroy]`, "1");
      } else if (attachment.file) {
        formData.set(`attachments_attributes[${index}][file]`, attachment.file);
      }
    });

    const url = `/api/v2/concierge/${ticket_slug}/ticket_finance_payments/${finance_payment_id}`,
      { error, response } = await PATCH<TicketFinancePaymentResponse>(
        url,
        formData,
      );

    return toFinancePaymentResult(error, response);
  }

  async deleteFinancePayment(
    ticket_slug: string,
    finance_payment_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/concierge/${ticket_slug}/ticket_finance_payments/${finance_payment_id}`,
      { error } = await DELETE<void>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async releaseFinancePayment(
    ticket_slug: string,
    finance_payment_id: number,
    release_note?: string,
  ): Promise<FetchResult<TicketFinancePayment>> {
    const url = `/api/v2/concierge/${ticket_slug}/ticket_finance_payments/${finance_payment_id}/release`,
      { error, response } = await POST<TicketFinancePaymentResponse>(
        url,
        release_note ? { release_note } : {},
      );

    return toFinancePaymentResult(error, response);
  }

  async getFinancePocOptions(
    ticket_slug: string,
  ): Promise<FetchResult<FinancePocOption[]>> {
    const url = `/api/v2/concierge/${ticket_slug}/ticket_finance_payments/finance_poc_options`,
      { error, response } = await GET<FinancePocOptionsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: response.options || [],
    };
  }
}

// create, update and release all answer { finance_payment }; a plain function, as callers destructure the methods
function toFinancePaymentResult(
  error: Error | null,
  response: TicketFinancePaymentResponse | null,
): FetchResult<TicketFinancePayment> {
  if (error) {
    return {
      error,
      response: null,
    };
  }

  if (!response) {
    return {
      error: new Error("Unknown error"),
      response: null,
    };
  }

  return {
    error: null,
    response: toFinancePayment(response.finance_payment),
  };
}

// documents come as DocumentDecorator hashes; map them to the approvals' document shape
function toFinancePayment(raw: any): TicketFinancePayment {
  return {
    ...raw,
    attachments: (raw.attachments || []).map((each: any) => ({
      id: each.id,
      file: each.link,
      link: each.link,
      document_name: each.title,
      document_identifier: each.document_identifier,
    })),
  };
}

export default ConciergeService;
